import { useEffect, useRef } from 'react'
import 'emoji-picker-element'

// Type for the emoji event from emoji-picker-element
interface EmojiClickEvent {
  detail: {
    unicode: string
    emoji: {
      annotation: string
      emoticon?: string
      group: number
      name: string
      order: number
      shortcodes: string[]
      tags: string[]
      unicode: string
    }
  }
}

// Compatible type with emoji-mart's BaseEmoji
export interface BaseEmoji {
  native: string
  id?: string
  name?: string
  colons?: string
  unified?: string
}

interface EmojiPickerProps {
  onSelect: (emoji: BaseEmoji) => void
  onClick?: () => void
  theme?: 'light' | 'dark' | 'auto'
  title?: string
  emoji?: string
}

// Extend HTMLElementTagNameMap to include emoji-picker
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'emoji-picker': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          class?: string
        },
        HTMLElement
      >
    }
  }
}

export const EmojiPicker = ({ onSelect, onClick, theme = 'dark' }: EmojiPickerProps) => {
  const pickerRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const picker = pickerRef.current
    if (!picker) return

    const handleEmojiClick = (event: Event) => {
      const emojiEvent = event as unknown as EmojiClickEvent
      const emoji: BaseEmoji = {
        native: emojiEvent.detail.unicode,
        name: emojiEvent.detail.emoji?.name,
        id: emojiEvent.detail.emoji?.shortcodes?.[0],
      }
      onSelect(emoji)
      onClick?.()
    }

    picker.addEventListener('emoji-click', handleEmojiClick)
    return () => {
      picker.removeEventListener('emoji-click', handleEmojiClick)
    }
  }, [onSelect, onClick])

  // Apply dark theme styles
  useEffect(() => {
    const picker = pickerRef.current
    if (!picker) return

    // Apply dark theme CSS variables
    if (theme === 'dark') {
      picker.style.setProperty('--background', '#1a1a1a')
      picker.style.setProperty('--border-color', '#333')
      picker.style.setProperty('--indicator-color', '#02f305')
      picker.style.setProperty('--input-border-color', '#444')
      picker.style.setProperty('--input-font-color', '#fff')
      picker.style.setProperty('--input-placeholder-color', '#888')
      picker.style.setProperty('--outline-color', '#02f305')
      picker.style.setProperty('--category-font-color', '#aaa')
      picker.style.setProperty('--button-active-background', '#333')
      picker.style.setProperty('--button-hover-background', '#252525')
    }
  }, [theme])

  return (
    <div className="emoji-picker-wrapper">
      <emoji-picker
        ref={pickerRef}
        class={theme === 'dark' ? 'dark' : 'light'}
      />
      <style>{`
        .emoji-picker-wrapper {
          position: relative;
          z-index: 1000;
          margin-top: 8px;
        }
        .emoji-picker-wrapper emoji-picker {
          --num-columns: 8;
          --emoji-size: 1.3rem;
          --emoji-padding: 0.3rem;
          height: 300px;
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
        }
        .emoji-picker-wrapper emoji-picker.dark {
          --background: #1a1a1a;
          --border-color: #333;
          --indicator-color: #02f305;
          --input-border-color: #444;
          --input-font-color: #fff;
          --input-placeholder-color: #888;
          --outline-color: #02f305;
          --category-font-color: #aaa;
          --button-active-background: #333;
          --button-hover-background: #252525;
        }
      `}</style>
    </div>
  )
}

// Also export as Picker for backwards compatibility
export const Picker = EmojiPicker

export default EmojiPicker
